// Konstanten rund um die Praxis-Status-Stufen.

/**
 * Anzeigename einer Praxis aus Hauptname + optionalem Namenszusatz.
 *
 * Lange Doppelnamen ("Röntgen-Institut Drewes+Partner / Praxis im
 * Franziskus-Hospital Harderberg") werden seit 1744300038 auf zwei Felder
 * verteilt. Überall, wo bisher schlicht `praxis.name` stand, gehört dieser
 * Helper hin — sonst verschwindet der Zusatz still aus der Anzeige.
 *
 * Ohne Zusatz kommt exakt der Name zurück, das Verhalten für den Bestand
 * ändert sich also nicht.
 */
export function praxisAnzeige(praxis) {
  if (!praxis) return '';
  const name = (praxis.name ?? '').trim();
  const zusatz = (praxis.name_zusatz ?? '').trim();
  if (!zusatz) return name;
  if (!name) return zusatz;
  return `${name} (${zusatz})`;
}

// Typ der Organisation: Arztpraxis oder Klinik. Werte sind in der
// PocketBase-Collection "praxen" als Select fest eingeschränkt
// (siehe pb_migrations/1744300000_init_praxen.js).
export const PRAXIS_TYP_OPTIONS = [
  { value: 'praxis', label: 'Arztpraxis' },
  { value: 'klinik', label: 'Klinik' },
];

export const PRAXIS_TYP_LABEL = Object.fromEntries(
  PRAXIS_TYP_OPTIONS.map((o) => [o.value, o.label])
);

export const PRAXIS_STATUS_OPTIONS = [
  { value: 'interessent', label: 'Interessent' },
  { value: 'aktiv', label: 'Aktiv' },
  { value: 'kunde', label: 'Kunde' },
  { value: 'inaktiv', label: 'Inaktiv' },
];

export const PRAXIS_STATUS_LABEL = Object.fromEntries(
  PRAXIS_STATUS_OPTIONS.map((o) => [o.value, o.label])
);

// Gelb / Blau / Grün / Grau gemäß Anforderung.
export const PRAXIS_STATUS_BADGE = {
  interessent: 'bg-amber-100 text-amber-800 ring-amber-200',
  aktiv: 'bg-blue-100 text-blue-800 ring-blue-200',
  kunde: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  inaktiv: 'bg-slate-100 text-slate-600 ring-slate-200',
};

// Default für Praxen, die vor der Status-Migration angelegt wurden.
export const PRAXIS_STATUS_DEFAULT = 'interessent';

export function praxisStatusOrDefault(value) {
  return value || PRAXIS_STATUS_DEFAULT;
}

// Wirtschaftsmodell: wie wird die Praxis/Klinik betrieben?
export const WIRTSCHAFTSMODELL_OPTIONS = [
  { value: 'gemeinschaft', label: 'Gemeinschaft' },
  { value: 'einzeln', label: 'Einzeln' },
  { value: 'unbekannt', label: 'Unbekannt' },
];

export const WIRTSCHAFTSMODELL_LABEL = Object.fromEntries(
  WIRTSCHAFTSMODELL_OPTIONS.map((o) => [o.value, o.label])
);

// Blau / Orange / Grau gemäß Anforderung.
export const WIRTSCHAFTSMODELL_BADGE = {
  gemeinschaft: 'bg-blue-100 text-blue-800 ring-blue-200',
  einzeln: 'bg-orange-100 text-orange-800 ring-orange-200',
  unbekannt: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export const WIRTSCHAFTSMODELL_DEFAULT = 'unbekannt';

export function wirtschaftsmodellOrDefault(value) {
  return value || WIRTSCHAFTSMODELL_DEFAULT;
}

// Praxis-Art: ist die Praxis reine Privatpraxis oder gemischt?
export const PRAXIS_ART_OPTIONS = [
  { value: 'privatpraxis', label: 'Privatpraxis' },
  { value: 'gemischt', label: 'Gemischt (Privat & Kassenpatient)' },
  { value: 'unbekannt', label: 'Unbekannt' },
];

export const PRAXIS_ART_LABEL = Object.fromEntries(
  PRAXIS_ART_OPTIONS.map((o) => [o.value, o.label])
);

// Kurzlabels für Badges/Tabellen, wo die ausführliche Variante zu lang wäre.
export const PRAXIS_ART_SHORT_LABEL = {
  privatpraxis: 'Privatpraxis',
  gemischt: 'Gemischt',
  unbekannt: 'Unbekannt',
};

// Lila / Blau / Grau gemäß Anforderung.
export const PRAXIS_ART_BADGE = {
  privatpraxis: 'bg-purple-100 text-purple-800 ring-purple-200',
  gemischt: 'bg-blue-100 text-blue-800 ring-blue-200',
  unbekannt: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export const PRAXIS_ART_DEFAULT = 'unbekannt';

export function praxisArtOrDefault(value) {
  return value || PRAXIS_ART_DEFAULT;
}

// Versorgungsform: wie ist die Versorgung organisiert? Eigene Achse neben
// typ (Praxis/Klinik), unternehmensform (Rechtsform) und wirtschaftsmodell
// (Betriebsmodell) — siehe 1744300037_praxen_versorgungsform.js.
// Alle Werte, die das PB-select kennt — inklusive "unbekannt", das nicht mehr
// zur Auswahl steht (siehe VERSORGUNGSFORM_OPTIONS). Diese Liste ist die
// Grundlage der Label-Maps, damit ein Bestandsdatensatz mit "unbekannt" sauber
// gerendert wird statt als Rohwert.
const VERSORGUNGSFORM_ALLE = [
  { value: 'einzelpraxis', label: 'Einzelpraxis' },
  { value: 'bag', label: 'BAG (Berufsausübungsgemeinschaft)' },
  { value: 'mvz', label: 'MVZ' },
  { value: 'klinik', label: 'Klinik' },
  { value: 'unbekannt', label: 'Unbekannt' },
];

/**
 * Auswählbare Versorgungsformen für Dropdowns und Filter.
 *
 * "unbekannt" fehlt hier bewusst: die leere Auswahl ("— nicht erfasst —")
 * deckt denselben Fall bereits ab, und zwei Optionen für "weiß ich nicht"
 * nebeneinander laden nur zu uneinheitlicher Pflege ein. Der Wert bleibt im
 * PB-select gültig — nichts geht verloren, er lässt sich nur nicht mehr neu
 * setzen. Eine Migration wäre hier das falsche Werkzeug: sie würde bestehende
 * Werte ungültig machen, statt nur die Eingabe zu führen.
 */
export const VERSORGUNGSFORM_OPTIONS = VERSORGUNGSFORM_ALLE.filter(
  (o) => o.value !== 'unbekannt'
);

export const VERSORGUNGSFORM_LABEL = Object.fromEntries(
  VERSORGUNGSFORM_ALLE.map((o) => [o.value, o.label])
);

// Kurzlabels für Badges/Tabellen, wo die ausführliche Variante zu lang wäre.
export const VERSORGUNGSFORM_SHORT_LABEL = {
  einzelpraxis: 'Einzelpraxis',
  bag: 'BAG',
  mvz: 'MVZ',
  klinik: 'Klinik',
  unbekannt: 'Unbekannt',
};

export const VERSORGUNGSFORM_BADGE = {
  einzelpraxis: 'bg-teal-100 text-teal-800 ring-teal-200',
  bag: 'bg-cyan-100 text-cyan-800 ring-cyan-200',
  mvz: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  klinik: 'bg-sky-100 text-sky-800 ring-sky-200',
  unbekannt: 'bg-slate-100 text-slate-600 ring-slate-200',
};

/**
 * Anzeigename der Versorgungsform.
 *
 * Bewusst KEIN versorgungsformOrDefault() analog zu praxisArtOrDefault():
 * bei diesem Feld ist leer ("nie erfasst") etwas anderes als "unbekannt"
 * ("nachgefragt, niemand wusste es"). Leere Werte bleiben leer und werden
 * als "—" dargestellt.
 */
export function versorgungsformAnzeige(value, { kurz = false } = {}) {
  if (!value) return '—';
  const map = kurz ? VERSORGUNGSFORM_SHORT_LABEL : VERSORGUNGSFORM_LABEL;
  return map[value] ?? value;
}

// Verbund-Zugehörigkeit (Liste von Iris, alphabetisch). Werte spiegeln das
// select "verbund" — Erweiterungen brauchen IMMER eine Migration, sonst weist
// PocketBase das Speichern ab (siehe 1744300039_praxen_verbund.js).
//
// "Kein Verbund" ist der Leerwert und steht bewusst nicht als eigener
// Select-Wert in der Liste — sonst gäbe es zwei Optionen mit derselben
// Bedeutung. Die Dropdowns hängen ihn als <option value=""> davor.
export const VERBUND_OPTIONS = [
  { value: 'acuvis', label: 'Acuvis' },
  { value: 'amedes', label: 'amedes Gruppe' },
  { value: 'artemed', label: 'Artemed Gruppe' },
  { value: 'corius', label: 'Corius Gruppe' },
  { value: 'curagita', label: 'Curagita' },
  { value: 'docdirekt', label: 'DocDirekt' },
  { value: 'evidia', label: 'Evidia' },
  { value: 'gesundheitsnetz_waldeck', label: 'Gesundheitsnetz Waldeck' },
  { value: 'lifelink', label: 'LifeLink' },
  { value: 'limbach', label: 'Limbach Gruppe' },
  { value: 'med360', label: 'Med360°' },
  { value: 'mediverbund', label: 'MEDIVERBUND' },
  { value: 'mum', label: 'MuM' },
  { value: 'oberberg', label: 'Oberberg Gruppe' },
  { value: 'oediv', label: 'OEDIV' },
  { value: 'radprax', label: 'RADPRAX Gruppe' },
  { value: 'sana_mvz', label: 'Sana MVZ' },
  { value: 'solimed', label: 'Solimed' },
  { value: 'synlab', label: 'Synlab' },
  { value: 'vivaneo', label: 'Vivaneo' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export const VERBUND_LABEL = Object.fromEntries(
  VERBUND_OPTIONS.map((o) => [o.value, o.label])
);

export const VERBUND_KEINER_LABEL = 'Kein Verbund';

/**
 * Anzeigename der Verbund-Zugehörigkeit.
 *
 * Bei "sonstiges" gewinnt der Freitext — sonst stünde in der Oberfläche
 * überall nur "Sonstiges", und die eigentliche Information wäre unsichtbar.
 * Ohne Wert kommt null zurück, damit die Aufrufer den Fall "Kein Verbund"
 * selbst entscheiden können (Detailseite blendet ihn ganz aus).
 */
export function verbundAnzeige(praxis) {
  const wert = praxis?.verbund;
  if (!wert) return null;
  if (wert === 'sonstiges') {
    const frei = (praxis.verbund_sonstiges ?? '').trim();
    return frei || VERBUND_LABEL.sonstiges;
  }
  return VERBUND_LABEL[wert] ?? wert;
}

// Unternehmensform: gängige Rechtsformen im ärztlichen Umfeld.
export const UNTERNEHMENSFORM_OPTIONS = [
  { value: 'einzelunternehmen', label: 'Einzelunternehmen' },
  { value: 'gbr', label: 'GbR' },
  { value: 'partg', label: 'PartG' },
  { value: 'partg_mbb', label: 'PartG mbB' },
  { value: 'gmbh', label: 'GmbH' },
  { value: 'mvz', label: 'MVZ' },
  { value: 'ag', label: 'AG' },
  { value: 'sonstiges', label: 'Sonstiges' },
  { value: 'unbekannt', label: 'Unbekannt' },
];

export const UNTERNEHMENSFORM_LABEL = Object.fromEntries(
  UNTERNEHMENSFORM_OPTIONS.map((o) => [o.value, o.label])
);

// Neutraler Badge, der in der Detailansicht ausreichend sichtbar ist —
// Unternehmensform ist kein "Alarm-Signal", sondern ein Stammdatum.
export const UNTERNEHMENSFORM_BADGE = {
  einzelunternehmen: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  gbr: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  partg: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  partg_mbb: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  gmbh: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  mvz: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  ag: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  sonstiges: 'bg-slate-100 text-slate-700 ring-slate-200',
  unbekannt: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export const UNTERNEHMENSFORM_DEFAULT = 'unbekannt';

export function unternehmensformOrDefault(value) {
  return value || UNTERNEHMENSFORM_DEFAULT;
}

// Praxissoftware: Slug im PB, Anzeige inkl. Firma im Frontend.
// Reihenfolge orientiert sich an Marktrelevanz (CGM-Familie zuerst).
export const SOFTWARE_OPTIONS = [
  { value: 'cgm_medistar', label: 'CGM Medistar (CompuGroup Medical)' },
  { value: 'cgm_turbomed', label: 'CGM Turbomed (CompuGroup Medical)' },
  { value: 'cgm_xconcept', label: 'CGM x.concept (CompuGroup Medical)' },
  { value: 'tomedo', label: 'Tomedo (Zollsoft)' },
  { value: 'doctolib', label: 'Doctolib (Doctolib)' },
  { value: 'dampsoft', label: 'Dampsoft (Dampsoft)' },
  { value: 'isynet', label: 'isynet (Evident)' },
  { value: 'albis', label: 'Albis (Albis)' },
  { value: 'm1_pro', label: 'M1 Pro (Medipool)' },
  { value: 'duria', label: 'Duria (Duria)' },
  { value: 'medistar', label: 'Medistar (Medistar)' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export const SOFTWARE_LABEL = Object.fromEntries(
  SOFTWARE_OPTIONS.map((o) => [o.value, o.label])
);

// Abrechnungsstelle: Wer rechnet die Privatliquidation ab?
export const ABRECHNUNGSSTELLE_OPTIONS = [
  { value: 'selbst', label: 'Selbst (Praxis rechnet selbst ab)' },
  { value: 'pvs_berlin_brandenburg', label: 'PVS Berlin/Brandenburg' },
  { value: 'pvs_schleswig_holstein_hamburg', label: 'PVS Schleswig-Holstein/Hamburg' },
  { value: 'pvs_suedwest', label: 'PVS Südwest' },
  { value: 'pvs_sachsen', label: 'PVS Sachsen' },
  { value: 'pvs_mosel_saar', label: 'PVS Mosel-Saar' },
  { value: 'pvs_limburg', label: 'PVS Limburg' },
  { value: 'pvs_niederbayern', label: 'PVS Niederbayern' },
  { value: 'pvs_rhein_ruhr', label: 'PVS Rhein-Ruhr' },
  { value: 'pvs_bayern', label: 'PVS Bayern' },
  { value: 'pvs_nordrhein', label: 'PVS Nordrhein' },
  { value: 'pvs_westfalen_lippe', label: 'PVS Westfalen-Lippe' },
  { value: 'privas', label: 'PRIVAS' },
  { value: 'unimed', label: 'Unimed' },
  { value: 'mcc_privadis', label: 'MCC (Privadis)' },
  { value: 'bfs_health_finance', label: 'BFS health finance' },
  { value: 'dr_guese_partner', label: 'Dr. Güse & Partner' },
  { value: 'mediorbis', label: 'Mediorbis' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export const ABRECHNUNGSSTELLE_LABEL = Object.fromEntries(
  ABRECHNUNGSSTELLE_OPTIONS.map((o) => [o.value, o.label])
);

// Factoring-Vertrag: welcher Partner stellt den Vertrag aus?
export const PRAXIS_VERTRAG_PARTNER_OPTIONS = [
  { value: 'as_bremen', label: 'AS Bremen' },
  { value: 'rhd_bremen', label: 'RHD Bremen' },
];

export const PRAXIS_VERTRAG_PARTNER_LABEL = Object.fromEntries(
  PRAXIS_VERTRAG_PARTNER_OPTIONS.map((o) => [o.value, o.label])
);

// Kurzlabel für den Badge in der Praxen-Liste — sonst wird die Zeile zu breit.
export const PRAXIS_VERTRAG_PARTNER_SHORT_LABEL = {
  as_bremen: 'AS',
  rhd_bremen: 'RHD',
};

// Factoring-Vertrag: in welchem Status steckt der Vertrag?
export const PRAXIS_VERTRAG_STATUS_OPTIONS = [
  { value: 'versendet', label: 'Versendet, wartet auf Rückläufer' },
  { value: 'unterschrieben', label: 'Unterschrieben / aktiv' },
  { value: 'abgelehnt', label: 'Abgelehnt' },
  { value: 'gekuendigt', label: 'Gekündigt / beendet' },
];

export const PRAXIS_VERTRAG_STATUS_LABEL = Object.fromEntries(
  PRAXIS_VERTRAG_STATUS_OPTIONS.map((o) => [o.value, o.label])
);

export const PRAXIS_VERTRAG_STATUS_SHORT_LABEL = {
  versendet: 'versendet',
  unterschrieben: 'aktiv',
  abgelehnt: 'abgelehnt',
  gekuendigt: 'gekündigt',
};

// Farbcode pro Status:
//   versendet      → amber, "in Bewegung"
//   unterschrieben → emerald, gut
//   abgelehnt      → rose, problematisch
//   gekuendigt     → slate, neutral / raus
export const PRAXIS_VERTRAG_STATUS_BADGE_PILL = {
  versendet: 'bg-amber-100 text-amber-800 ring-amber-200',
  unterschrieben: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  abgelehnt: 'bg-rose-100 text-rose-800 ring-rose-200',
  gekuendigt: 'bg-slate-100 text-slate-700 ring-slate-200',
};

// Welche Vertrags-Felder gelten überhaupt als "etwas gepflegt"? Wenn alle
// leer sind, zeigt die Detailseite den Empty-State. Wird auch beim Filter
// "Kein Vertrag" benutzt.
export const PRAXIS_VERTRAG_FELDER = [
  'vertrag_partner',
  'vertrag_status',
  'vertrag_kundennummer',
  'vertrag_versendet_am',
  'vertrag_beginn_am',
  'vertrag_ende_am',
  'vertrag_medifin_gebuehr_prozent',
  'vertrag_factoring_gebuehr_prozent',
  'vertrag_grundgebuehr_eur',
];

export function hatVertrag(praxis) {
  if (!praxis) return false;
  return PRAXIS_VERTRAG_FELDER.some((feld) => {
    const v = praxis[feld];
    if (v === null || v === undefined || v === '') return false;
    return true;
  });
}

// Filial-Hierarchie: zählt, wie viele Filialen (Töchter) zu einer Praxis
// gehören. Wird vor dem Löschen geprüft (Hauptpraxen mit Filialen können
// nicht gelöscht werden) und auf der Detailseite für den Banner verwendet.
//
// Erwartet eine pb-Instanz (lib/pocketbase.js), damit der Aufrufer den
// Auth-Context kontrolliert.
export async function fetchFilialenCount(pb, praxisId) {
  if (!praxisId) return 0;
  try {
    const resp = await pb.collection('praxen').getList(1, 1, {
      filter: pb.filter('mutter_praxis = {:id}', { id: praxisId }),
      fields: 'id',
    });
    return resp.totalItems;
  } catch {
    return 0;
  }
}
